import { createFileRoute, Link, Outlet, redirect, useRouter, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import {
  LayoutDashboard, ListChecks, FileText, Palette, Image as ImageIcon,
  MessageSquare, CalendarDays, CreditCard, ShieldCheck, Bell, User, Receipt,
  Menu, X, LogOut, MoreHorizontal, BellRing,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BottomTabBar, type BottomTab } from "@/components/mobile/BottomTabBar";
import { MoreSheet } from "@/components/mobile/MoreSheet";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { InstallPWAPrompt } from "@/components/pwa/InstallPWAPrompt";
import { usePortalPWA } from "@/hooks/use-portal-pwa";
import { PushEnableCard } from "@/components/push/PushEnableCard";

export const Route = createFileRoute("/_authenticated/portal")({
  head: () => ({ meta: [{ title: "Customer Portal — Michael's Painting" }, { name: "robots", content: "noindex,nofollow" }] }),
  beforeLoad: ({ location }) => {
    if (location.pathname === "/portal" || location.pathname === "/portal/") {
      throw redirect({ to: "/portal/dashboard" });
    }
  },
  component: PortalLayout,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="min-h-screen bg-[#030303] text-zinc-300 grid place-items-center px-6">
        <div className="max-w-md text-center">
          <p className="mb-3 text-red-400">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="bg-amber-300 text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
        </div>
      </div>
    );
  },
});

const NAV = [
  { to: "/portal/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/portal/project", label: "Project Timeline", icon: ListChecks },
  { to: "/portal/proposal", label: "Proposal & Estimate", icon: FileText },
  { to: "/portal/colors", label: "Paint Colors", icon: Palette },
  { to: "/portal/gallery", label: "Photo Gallery", icon: ImageIcon },
  { to: "/portal/documents", label: "Documents", icon: FileText },
  { to: "/portal/messages", label: "Messages", icon: MessageSquare },
  { to: "/portal/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/portal/invoices", label: "Invoices", icon: Receipt },
  { to: "/portal/payments", label: "Payments", icon: CreditCard },
  { to: "/portal/warranty", label: "Warranty", icon: ShieldCheck },
  { to: "/portal/notifications", label: "Notifications", icon: Bell },
  { to: "/portal/notification-settings", label: "Notification Settings", icon: BellRing },
  { to: "/portal/profile", label: "Profile", icon: User },
];

function PortalLayout() {
  usePortalPWA("portal");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/portal";
  };

  const tabs: BottomTab[] = [
    { to: "/portal/dashboard", label: "Home", icon: LayoutDashboard },
    { to: "/portal/project", label: "Project", icon: ListChecks },
    { to: "/portal/messages", label: "Chat", icon: MessageSquare },
    { to: "/portal/schedule", label: "Schedule", icon: CalendarDays },
    { to: "#more", label: "More", icon: MoreHorizontal, onClick: () => setMoreOpen(true), match: () => moreOpen },
  ];

  return (
    <div className="themed-shell min-h-screen bg-[#030303] text-zinc-200 flex">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 safe-top">
        <button onClick={() => setMobileOpen(true)} className="touch-target grid place-items-center -ml-2 text-zinc-200" aria-label="Open menu">
          <Menu className="size-5" />
        </button>
        <Link to="/portal/dashboard" className="font-serif text-base text-white truncate">
          Michael's <span className="italic text-amber-200">Portal</span>
        </Link>
        <Link to="/portal/notifications" className="touch-target grid place-items-center -mr-2 text-zinc-300" aria-label="Notifications">
          <Bell className="size-5" />
        </Link>
      </div>

      {/* Drawer scrim */}
      {mobileOpen && (
        <button
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
        />
      )}

      <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-72 shrink-0 border-r border-white/10 bg-gradient-to-b from-[#0a0a0a] to-[#050505] transition-transform`}>
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-300 text-[10px] tracking-[0.3em] uppercase font-semibold">Customer Portal</p>
              <h2 className="font-serif text-xl text-white mt-1">Michael's Painting</h2>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/portal/notifications"
                onClick={() => setMobileOpen(false)}
                className="touch-target grid place-items-center rounded-full text-zinc-300 hover:text-amber-300 hover:bg-white/5 transition"
                aria-label="Activity & notifications"
                title="Activity & notifications"
              >
                <Bell className="size-5" />
              </Link>
              <button onClick={() => setMobileOpen(false)} className="lg:hidden touch-target grid place-items-center text-zinc-400" aria-label="Close">
                <X className="size-5" />
              </button>
            </div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-0.5 overflow-y-auto h-[calc(100vh-180px)]">
          {NAV.map((item) => {
            const active = pathname === item.to || pathname.startsWith(item.to + "/");
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all touch-target ${
                  active ? "bg-amber-300/10 text-amber-200 border border-amber-300/20 shadow-[0_0_20px_-12px_rgba(251,191,36,0.5)]"
                         : "text-zinc-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
                }`}>
                <Icon className="size-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 px-4 py-3 space-y-2 bg-[#050505]">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-[11px] text-zinc-400 hover:text-amber-300">← Back to site</Link>
            <ThemeToggle />
          </div>
          <button onClick={signOut} className="w-full flex items-center gap-2 text-xs text-zinc-400 hover:text-white px-2 py-2 rounded-lg hover:bg-white/5 transition">
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="px-4 lg:px-10 py-6 lg:py-10 pt-20 lg:pt-10 safe-pb-nav lg:pb-10 max-w-6xl">
          <Outlet />
        </div>
      </main>

      <BottomTabBar tabs={tabs} />

      <InstallPWAPrompt storageKey="mp_pwa_prompt_portal" portalName="Customer Portal" />
      <PushEnableCard portal="customer" settingsHref="/portal/notification-settings" />

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="grid grid-cols-3 gap-2 p-2">
          {NAV.filter((n) => !["/portal/dashboard","/portal/project","/portal/messages","/portal/schedule"].includes(n.to)).map((n) => {
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMoreOpen(false)}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/5 border border-white/10 p-4 text-center touch-target"
              >
                <Icon className="size-5 text-amber-300" />
                <span className="text-[11px] text-zinc-200 leading-tight">{n.label}</span>
              </Link>
            );
          })}
        </div>
        <div className="px-4 py-4 border-t border-white/10 mt-2 flex flex-col gap-3">
          <Link to="/" onClick={() => setMoreOpen(false)} className="text-sm text-zinc-400">← Back to website</Link>
          <button onClick={signOut} className="inline-flex items-center gap-2 text-sm text-zinc-300">
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      </MoreSheet>
    </div>
  );
}